import React, {useEffect} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams, Link } from 'react-router-dom';
import { Breadcrumb, BackdropCircular } from 'components';
import {Tabs, Tab, TextField} from '@material-ui/core';
import { TabContext } from '@material-ui/lab';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { getHelpCategoriesApi, getHelpSubCategoriesApi, getHelpTopicsApi, getAllHelpTopicsApi } from 'api';
import HelpTabs from './HelpTabs';
import ListOfHelpArticles from './ListOfHelpArticles';


const useStyles = makeStyles({
  search: {
    padding: 0,
    margin: 0,
    borderRadius: 6,
  }
});


export default function Help() {
  const history = useHistory();
  const {categoryUrl, subCategoryUrl, topicUrl} = useParams<{categoryUrl?: string; subCategoryUrl?: string; topicUrl?: string}>();
  const {
    helpCategories,
    helpSubCategories,
    helpTopics,
    allHelpTopics,
    isLoadingAllHelpTopics,
    isLoadingHelpTopics,
    isLoadingHelpCategories,
    isLoadingHelpSubCategories
  }: IHelpState = useSelector((state: IReduxState) => state.help);
  const classes = useStyles();
  const dispatch = useDispatch();

  useEffect(() => {
    getHelpCategoriesApi({ dispatch });
    getAllHelpTopicsApi({ dispatch });
  }, []);

  useEffect(() => {
    if (!categoryUrl && helpCategories.length) {
      history.push(`/help/${helpCategories?.[0].path}`);
    }
  }, [helpCategories]);

  useEffect(() => {
    if (!subCategoryUrl && helpSubCategories.length) {
      history.push(`/help/${categoryUrl || helpCategories?.[0].path}/${helpSubCategories?.[0].path}`);
    }
  }, [helpSubCategories]);

  useEffect(() => {
    getHelpSubCategoriesApi({ dispatch, categoryUrl });
  }, [categoryUrl]);

  useEffect(() => {
    getHelpTopicsApi({ dispatch, subCategoryUrl, categoryUrl });
  }, [subCategoryUrl]);

  const selectedCategory = helpCategories.find((s) => s.path === categoryUrl);
  const selectedSubCategory = helpSubCategories.find((s) => s.path === subCategoryUrl);
  const selectedTopic = helpTopics.find((s) => s.path === topicUrl);

  const handleSearch = (article) => {
    history.push(`/help/${article.category.path}/${article.subCategory.path}/${article.path}`);
  };

  return (
    <div className="flex-grow h-full min-h-full-screen scroll-y">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            {
              name: 'Help',
              path: '/help'
            },
            {
              name: selectedCategory?.title,
              path: `/help/${categoryUrl}/${subCategoryUrl}`
            },
            {
              name: selectedSubCategory?.title || selectedTopic?.title,
              path: `/help/${categoryUrl}/${subCategoryUrl}/${topicUrl}`
            }
          ]}
        />
        <div className="p-10 mt-8">
          <div>
            <h3>How can we help you?</h3>
            <Autocomplete
              size="small"
              className="max-w-280 mt-2"
              freeSolo
              disableClearable
              groupBy={(option) => option.subCategory.title}
              options={allHelpTopics}
              getOptionLabel={(option) => option.title}
              onChange={(e, option) => handleSearch(option)}
              renderInput={(params) => (
                <TextField
                  className={`${classes.search}`}
                  variant="outlined"
                  {...params}
                  label="Search Knowledge base"
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                />
              )}
            />
          </div>
          <div>
            <TabContext value={categoryUrl}>
              <Tabs
                value={categoryUrl}
                indicatorColor="primary"
                textColor="primary"
              >
                {helpCategories.map((helpCategory) => (
                  <Tab
                    key={helpCategory.path}
                    className="capitalize font-medium"
                    value={helpCategory.path}
                    label={helpCategory.title}
                    component={Link}
                    to={`/help/${helpCategory.path}`}
                  />
                ))}
              </Tabs>
              <HelpTabs />
            </TabContext>
          </div>
          <div className="w-full">
            <ListOfHelpArticles />
          </div>
        </div>
        <BackdropCircular
          loading={isLoadingAllHelpTopics || isLoadingHelpTopics || isLoadingHelpCategories || isLoadingHelpSubCategories}
        />
      </div>
    </div>
  );
}
